import React, { useState, useEffect } from "react";
import { trackEvent } from "../../../lib/matomo";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { RouterPath } from "../../../assets/dictionary/RouterPath";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import SignUpFormService from "./SignUpFormService";
import { GoogleLogin } from "@react-oauth/google";
import DataService from "../../../components/auth/Service";
import LogoBlue from "../../../assets/icons/peppa_logo_blue.svg";
import { getPostAuthRedirectTarget } from "../../../lib/authRedirect";

export default function SignUpFormModern({
  embedded = false,
  showGoogleButton = true,
  showLogo = true,
  noCard = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    checks: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
    },
  });

  const passwordRegex = /^(?=.*\p{Ll})(?=.*\p{Lu})(?=.*\d).{8,}$/u;

  useEffect(() => {
    const checks = {
      length: formData.password.length >= 8,
      uppercase: /[A-Z]/.test(formData.password),
      lowercase: /[a-z]/.test(formData.password),
      number: /\d/.test(formData.password),
      special: /[@$!%*?&]/.test(formData.password),
    };

    const score = Object.values(checks).filter(Boolean).length;
    setPasswordStrength({ score, checks });
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value.trim(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!acceptedTerms) {
      setError("Vous devez accepter les conditions d'utilisation");
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      setIsLoading(false);
      return;
    }

    if (!passwordRegex.test(formData.password)) {
      setError("Le mot de passe ne respecte pas les critères de sécurité");
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email.toLowerCase(),
        password: formData.password,
      };

      const response = await SignUpFormService.postSignUp(payload);
      if (response.status === 200) {
        try {
          trackEvent("auth", "signup_success");
        } catch (_) {}
        navigate(RouterPath.SIGNUP_MAIL_SENT, {
          state: { email: payload.email },
        });
      } else {
        setError("Erreur lors de l'inscription");
        try {
          trackEvent("auth", "signup_error");
        } catch (_) {}
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setError("Un utilisateur avec cet email existe déjà");
        try {
          trackEvent("auth", "signup_email_exists");
        } catch (_) {}
      } else if (status === 401) {
        setError("Cet email ne fait pas partie de la liste des bêta-testeurs.");
        try {
          trackEvent("auth", "signup_not_in_beta");
        } catch (_) {}
      } else {
        setError(err?.response?.data?.message || "Erreur lors de l'inscription");
        try {
          trackEvent("auth", "signup_exception");
        } catch (_) {}
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 2) return "text-red-500";
    if (passwordStrength.score <= 3) return "text-yellow-500";
    return "text-green-500";
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setError("");
    try {
      const response = await DataService.loginWithGoogle(credentialResponse.credential);
      if (response.status === 200) {
        navigate(getPostAuthRedirectTarget(location), { replace: true });
      } else {
        setError("Inscription/connexion Google échouée");
      }
    } catch (err) {
      setError("Inscription/connexion Google échouée");
    } finally {
      setIsLoading(false);
    }
  };

  const containerClass = embedded
    ? "w-full"
    : "min-h-screen flex items-center justify-center p-4 app-page-gradient";

  const widthClass = embedded ? "w-full max-w-xl mx-auto" : "w-full max-w-md";

  const content = (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="first_name" className="text-foreground">
              Prénom *
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="first_name"
                name="first_name"
                type="text"
                placeholder="Prénom"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="last_name" className="text-foreground">
              Nom *
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="last_name"
                name="last_name"
                type="text"
                placeholder="Nom"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="pl-10"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-foreground">
            Email *
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="votre@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="pl-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-foreground">
            Mot de passe *
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Votre mot de passe"
              value={formData.password}
              onChange={handleChange}
              required
              className="pl-10 pr-10"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>

          {formData.password && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Force du mot de passe:</span>
                <span className={`text-sm font-medium ${getPasswordStrengthColor()}`}>
                  {passwordStrength.score <= 2 ? "Faible" : passwordStrength.score <= 3 ? "Moyen" : "Fort"}
                </span>
              </div>
              <div className="space-y-1">
                {Object.entries(passwordStrength.checks).map(([key, passed]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    {passed ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className={passed ? "text-green-600" : "text-red-600"}>
                      {key === "length" && "Au moins 8 caractères"}
                      {key === "uppercase" && "Au moins une majuscule"}
                      {key === "lowercase" && "Au moins une minuscule"}
                      {key === "number" && "Au moins un chiffre"}
                      {key === "special" && "Au moins un caractère spécial (@$!%*?&)"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-foreground">
            Confirmer le mot de passe *
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirmez votre mot de passe"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="pl-10 pr-10"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="terms"
            checked={acceptedTerms}
            onCheckedChange={setAcceptedTerms}
          />
          <Label htmlFor="terms" className="text-sm text-foreground">
            J'ai lu et j'accepte les{" "}
            <Link
              to="https://peppacorp.fr/cgu"
              className="text-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              conditions d'utilisation
            </Link>
            *
          </Label>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? "Création du compte..." : "Créer un compte"}
        </Button>
      </form>

      {showGoogleButton && (
        <div>
          <div className="relative w-full">
            <Button
              type="button"
              variant="outline"
              className="w-full border-border/50 bg-card/90 backdrop-blur-sm hover:bg-accent/50 flex items-center justify-center gap-2 text-foreground"
              disabled={isLoading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-4 w-4">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.108,18.961,14,24,14c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.022C9.505,39.556,16.227,44,24,44z" />
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.236-2.231,4.166-3.994,5.571 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
              </svg>
              <span>S’inscrire avec Google</span>
            </Button>
            <div className="google-overlay">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError("Inscription/connexion Google échouée")}
                useOneTap={false}
                theme="outline"
                shape="rectangular"
                text="signup_with"
                size="large"
                width="320"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (noCard) {
    return (
      <div className={containerClass}>
        <div className={widthClass}>
          {showLogo && (
            <div className="flex items-center justify-center mb-2">
              <img src={LogoBlue} alt="Peppa" className="h-6" />
            </div>
          )}
          <h2 className="text-xl md:text-2xl font-bold text-foreground text-center mb-4">
            Créer un compte
          </h2>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={widthClass}>
        <Card className="glass-card border-muted shadow-2xl">
          <CardHeader className="text-center space-y-2">
            {showLogo && (
              <div className="flex items-center justify-center mb-2">
                <img src={LogoBlue} alt="Peppa" className="h-6" />
              </div>
            )}
            <CardTitle className="text-2xl font-bold text-foreground">
              Créer un compte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">{content}</CardContent>
        </Card>
      </div>
    </div>
  );
}


